import React from "react";
import { FaStar } from "react-icons/fa";

const RatingDetails = ({ ratings, loading = false }) => {
  const avg = Number(ratings?.averageRating || 0);
  const total = Number(ratings?.totalRatings || 0);
  const breakdown = ratings?.ratingsBreakdown || {};
  const list = ratings?.ratings || [];

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6">
        <p className="text-gray-600">Loading reviews...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6">
      {/* Summary */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <span className="text-3xl font-extrabold text-gray-900">
            {avg.toFixed(1)}
          </span>

          <div className="flex items-center gap-1">
            <FaStar className="text-yellow-400" />
            <span className="text-sm text-gray-600">
              ({total} {total === 1 ? "review" : "reviews"})
            </span>
          </div>
        </div>

        {total === 0 ? (
          <span className="text-sm text-gray-500">No ratings yet</span>
        ) : (
          <span className="text-sm font-semibold text-gray-900">
            Rating breakdown
          </span>
        )}
      </div>

      {/* Breakdown */}
      {total > 0 && (
        <div className="mt-5 space-y-2">
          {[5, 4, 3, 2, 1].map((star) => {
            const count = Number(breakdown?.[star] || 0);
            const percent = total ? (count / total) * 100 : 0;

            return (
              <div key={star} className="flex items-center gap-3">
                <span className="w-10 text-sm font-semibold text-gray-700">
                  {star}★
                </span>

                <div className="flex-1 bg-gray-200 h-2 rounded-full overflow-hidden">
                  <div
                    className="bg-yellow-400 h-2 rounded-full"
                    style={{ width: `${percent}%` }}
                  />
                </div>

                <span className="w-10 text-right text-sm text-gray-700">
                  {count}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Reviews */}
      <div className="mt-6">
        <h3 className="text-lg font-bold text-gray-900">Customer reviews</h3>

        {list.length === 0 ? (
          <div className="mt-3 rounded-2xl border border-gray-100 bg-gray-50 p-4">
            <p className="text-gray-700 font-semibold">No reviews yet</p>
            <p className="text-sm text-gray-500 mt-1">
              Be the first to rate this product.
            </p>
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            {list.map((r) => (
              <div
                key={r._id}
                className="rounded-2xl border border-gray-100 p-4 sm:p-5"
              >
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 truncate">
                      {r?.user?.fullName || "Anonymous"}
                    </p>
                    <p className="text-sm text-gray-500 break-words">
                      {r?.user?.email || ""}
                    </p>
                  </div>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: Number(r?.rating || 0) }).map((_, i) => (
                      <FaStar key={i} className="text-yellow-400 text-sm" />
                    ))}
                  </div>
                </div>

                {/* Optional comment field if you add it later */}
                {r?.comment && (
                  <p className="mt-3 text-sm text-gray-700 leading-relaxed">
                    {r.comment}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RatingDetails;
